import { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Typography, Tag, Button, Spin, Descriptions, message, Space, Alert, Empty
} from 'antd';
import {
  PhoneOutlined, CarOutlined, ArrowLeftOutlined, UserOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { supabase, getSignedUrl } from '@ubus/shared';
import type { Trip } from '@ubus/shared';
import dayjs from 'dayjs';
import {
  formatTime,
  formatCapacity,
  formatPhone,
  formatVehiclePublicLabel,
  getClientTripDisplayColor,
  hasFullCrew,
} from '@ubus/shared';
import { translateClientTripStatus } from '../utils/clientStatusLabels';

const { Title, Text } = Typography;

function TripDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);
  const [attachmentLoadError, setAttachmentLoadError] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const fetchTripDetail = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('trips')
        .select('*, vehicles(*), drivers(*), organizations(*)')
        .eq('id', id)
        .single();

      if (error) {
        message.error(error.message);
      } else {
        setTrip(data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) void fetchTripDetail();
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const paths = trip?.attachment_urls ?? [];
    if (!paths.length) {
      setAttachmentUrls([]);
      setAttachmentLoadError(false);
      return;
    }
    void (async () => {
      const urls: string[] = [];
      let failed = 0;
      for (const path of paths) {
        if (path.startsWith('http')) {
          urls.push(path);
          continue;
        }
        const signed = await getSignedUrl('uploads', path, 3600);
        if (signed) urls.push(signed);
        else failed += 1;
      }
      if (!cancelled) {
        setAttachmentUrls(urls);
        setAttachmentLoadError(failed > 0 && urls.length === 0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trip?.id, trip?.attachment_urls]);

  useEffect(() => {
    if (!id) return;

    const channel = supabase
      .channel(`trip-detail-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'trips',
          filter: `id=eq.${id}`,
        },
        (payload) => {
          const updated = payload.new as Trip;
          setTrip((prev) => (prev ? { ...prev, ...updated } : prev));
          void fetchTripDetail();
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [id]);

  const statusAlert = useMemo(() => {
    if (!trip) return null;
    if (trip.status === 'in_progress') {
      return {
        type: 'success' as const,
        message: t('trips.alerts.inProgress'),
        description: trip.vehicle_id && trip.vehicles
          ? t('trips.alerts.inProgressDesc')
          : t('trips.alerts.inProgressDescNoVehicle'),
      };
    }
    if (trip.status === 'dispatched' && hasFullCrew(trip)) {
      return {
        type: 'info' as const,
        message: t('trips.alerts.crewAssigned'),
        description: t('trips.alerts.crewAssignedDesc'),
      };
    }
    const vehicleOnlyAssigned = Boolean(trip.vehicle_id && !trip.driver_id);
    if (
      (trip.status === 'scheduled' || trip.status === 'dispatched') &&
      vehicleOnlyAssigned
    ) {
      return {
        type: 'info' as const,
        message: t('trips.alerts.vehiclePending'),
        description: t('trips.alerts.vehiclePendingDesc'),
      };
    }
    if (trip.status === 'dispatched') {
      return {
        type: 'warning' as const,
        message: t('trips.alerts.confirmed'),
        description: t('trips.alerts.confirmedDesc'),
      };
    }
    if (trip.status === 'scheduled') {
      return {
        type: 'info' as const,
        message: t('trips.alerts.scheduled'),
        description: t('trips.alerts.scheduledDesc'),
      };
    }
    return null;
  }, [trip, t]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!trip) {
    return (
      <Empty
        description={t('trips.notFound')}
        style={{ padding: 48 }}
      >
        <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')}>
          {t('trips.backToToday')}
        </Button>
      </Empty>
    );
  }

  const hasDriver = Boolean(trip.driver_id && trip.drivers);
  const hasVehicle = Boolean(trip.vehicle_id && trip.vehicles);
  const isTrackable = trip.status === 'dispatched' || trip.status === 'in_progress';
  const statusLabel = translateClientTripStatus(t, trip);
  const statusColor = getClientTripDisplayColor(trip);

  return (
    <div>
      <div className="client-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            aria-label={t('common.back')}
          />
          <div>
            <Title level={3} className="client-page-title">
              {t('trips.detailTitle')}
            </Title>
            <Text className="client-page-subtitle">
              {dayjs(trip.date).format('ddd, DD MMM YYYY')}
              {' · '}
              {t('trips.pickupAt', { time: formatTime(trip.pickup_time) })}
              {trip.estimated_dropoff_time
                ? ` · ${t('trips.dropoffAt', { time: formatTime(trip.estimated_dropoff_time) })}`
                : ''}
            </Text>
          </div>
          <Tag color={statusColor} style={{ fontSize: 13, padding: '2px 12px', marginInlineStart: 4 }}>
            {statusLabel}
          </Tag>
        </div>

        <div className="client-actions">
          {isTrackable && (
            <Button
              type="primary"
              size="middle"
              icon={<CarOutlined />}
              onClick={() => navigate(`/map/${trip.id}`)}
            >
              {t('trips.trackVehicle')}
            </Button>
          )}
        </div>
      </div>

      {statusAlert && (
        <Alert
          type={statusAlert.type}
          message={statusAlert.message}
          description={statusAlert.description}
          showIcon
          style={{ marginBottom: 20, borderRadius: 10 }}
        />
      )}

      <Card styles={{ body: { padding: 0 } }} style={{ borderRadius: 12, borderColor: 'var(--client-border)' }}>
        <div style={{ padding: 20 }}>
          <Descriptions
            column={{ xs: 1, sm: 2 }}
            bordered
            size="middle"
            labelStyle={{ width: 140, background: '#f8fafc' }}
          >
            <Descriptions.Item label={t('common.date')}>
              {dayjs(trip.date).format('DD MMM YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label={t('trips.estimatedPickup')}>
              {formatTime(trip.pickup_time)}
            </Descriptions.Item>
            <Descriptions.Item label={t('trips.estimatedDropoff')}>
              {trip.estimated_dropoff_time
                ? formatTime(trip.estimated_dropoff_time)
                : t('common.pending')}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.pickup')} span={2}>
              {trip.pickup_address}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.dropoff')} span={2}>
              {trip.dropoff_address}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.passengers')}>
              {trip.passenger_count}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.statusLabel')}>
              <Tag color={statusColor}>{statusLabel}</Tag>
            </Descriptions.Item>
            {trip.special_notes && (
              <Descriptions.Item label={t('common.notes')} span={2}>
                {trip.special_notes}
              </Descriptions.Item>
            )}
            {trip.cancelled_reason && (
              <Descriptions.Item label={t('trips.cancellation')} span={2}>
                <Text type="danger">{trip.cancelled_reason}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>

          {((trip.attachment_urls?.length ?? 0) > 0 || attachmentUrls.length > 0) && (
            <div className="client-trip-attachments" style={{ marginTop: 20 }}>
              <Text
                strong
                style={{
                  display: 'block',
                  marginBottom: 12,
                  fontSize: 13,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'var(--client-ink-secondary)',
                }}
              >
                {t('trips.tripPhotos')}
              </Text>
              {attachmentLoadError ? (
                <Text type="secondary">{t('trips.photosLoadFailed')}</Text>
              ) : attachmentUrls.length === 0 ? (
                <Text type="secondary">{t('trips.photosLoading')}</Text>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                    gap: 10,
                  }}
                >
                  {attachmentUrls.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer">
                      <img
                        src={url}
                        alt={t('trips.photoAlt')}
                        style={{
                          width: '100%',
                          aspectRatio: '1',
                          objectFit: 'cover',
                          borderRadius: 10,
                          display: 'block',
                          border: '1px solid var(--client-border, #e5e7eb)',
                        }}
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: '0 20px 20px' }}>
          <Text
            strong
            style={{
              display: 'block',
              marginBottom: 12,
              fontSize: 13,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'var(--client-ink-secondary)',
            }}
          >
            {t('trips.assignedCrew')}
          </Text>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Card
              size="small"
              className="client-crew-card"
              style={{ flex: 1, minWidth: 240 }}
              title={
                <Space>
                  <UserOutlined />
                  {t('common.driver')}
                </Space>
              }
            >
              {hasDriver ? (
                <Space direction="vertical" size={6}>
                  <Text strong style={{ fontSize: 16 }}>{trip.drivers!.name}</Text>
                  <a href={`tel:${trip.drivers!.phone}`} style={{ color: 'var(--client-brand)' }}>
                    <PhoneOutlined style={{ marginRight: 8 }} />
                    {formatPhone(trip.drivers!.phone)}
                  </a>
                  {trip.drivers!.license_number && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('trips.licence', { number: trip.drivers!.license_number })}
                    </Text>
                  )}
                </Space>
              ) : (
                <Text type="secondary">
                  {trip.status === 'scheduled' || trip.status === 'dispatched'
                    ? t('trips.awaitingDriver')
                    : t('trips.noDriver')}
                </Text>
              )}
            </Card>

            <Card
              size="small"
              className="client-crew-card"
              style={{ flex: 1, minWidth: 240 }}
              title={
                <Space>
                  <CarOutlined />
                  {t('common.vehicle')}
                </Space>
              }
            >
              {hasVehicle ? (
                <Space direction="vertical" size={6}>
                  <Text strong style={{ fontSize: 16 }}>
                    {formatVehiclePublicLabel(trip.vehicles!)}
                  </Text>
                  {trip.vehicles!.model && (
                    <Text type="secondary">{trip.vehicles!.model}</Text>
                  )}
                  <Text type="secondary">{formatCapacity(trip.vehicles!.capacity)}</Text>
                </Space>
              ) : (
                <Text type="secondary">
                  {trip.status === 'scheduled' || trip.status === 'dispatched'
                    ? t('trips.awaitingVehicle')
                    : t('trips.noVehicle')}
                </Text>
              )}
            </Card>
          </div>
        </div>

        {trip.organizations && (
          <div style={{ padding: '0 20px 20px' }}>
            <Text
              strong
              style={{
                display: 'block',
                marginBottom: 12,
                fontSize: 13,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--client-ink-secondary)',
              }}
            >
              {t('trips.organization')}
            </Text>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label={t('trips.orgName')}>{trip.organizations.name}</Descriptions.Item>
              <Descriptions.Item label={t('trips.orgContact')}>
                {trip.organizations.contact_person}
                {trip.organizations.contact_phone
                  ? ` · ${formatPhone(trip.organizations.contact_phone)}`
                  : ''}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Card>
    </div>
  );
}

export default TripDetail;
